class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length === 0) {
      this.clear();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    // Start loading the collection list now so it's ready by the first search
    if (PredictiveSearch.useFallback) this.loadCollections();
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.clear(event);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    // One flat list across the collections and products listboxes, so the arrow keys can move between them
    const allElements = Array.from(this.predictiveSearchResults.querySelectorAll('li[role="option"]'));
    const selectedIndex = allElements.indexOf(selectedElement);
    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;
    if (!activeElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = allElements[selectedIndex + 1] || allElements[0];
    } else if (moveUp) {
      activeElement = allElements[selectedIndex - 1] || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    // Cancel the previous request so a slow reply for an older term can't overwrite newer results
    if (this.abortController) this.abortController.abort();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const useFallback = PredictiveSearch.useFallback;
    const sectionId = useFallback ? 'predictive-search-fallback' : 'predictive-search';
    const url = useFallback
      ? `${window.routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product&${encodeURIComponent('options[prefix]')}=last&section_id=${sectionId}`
      : `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection&${encodeURIComponent('resources[limit]')}=${perPage}&section_id=${sectionId}`;
    const collectionsPromise = useFallback ? this.loadCollections() : Promise.resolve(null);

    fetch(url, { signal })
      .then((response) => {
        // Predictive search answers 417 "Unsupported buyer locale" for some shop languages (e.g. Arabic);
        // switch to the full search for the rest of the page view
        if (response.status === 417 && !useFallback) {
          PredictiveSearch.useFallback = true;
          return null;
        }

        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (text === null) return this.getSearchResults(searchTerm);

        return collectionsPromise.then((collections) => {
          if (signal.aborted) return;

          const section = new DOMParser().parseFromString(text, 'text/html').querySelector(`#shopify-section-${sectionId}`);
          if (collections) this.addCollectionResults(section, collections, searchTerm, perPage);

          const resultsMarkup = section.innerHTML;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);

          if (this.modal) this.modal.classList.add('searching');
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
        throw error;
      });
  }

  // The full search used by the fallback can't return collections, so load the shop's collection list
  // once per visit (about 14 KB compressed) and match titles in the browser
  loadCollections() {
    if (PredictiveSearch.collectionsPromise) return PredictiveSearch.collectionsPromise;

    const root = window.routes.root_url.replace(/\/$/, '');
    const storageKey = `predictive-search-collections:${root}`;

    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) return (PredictiveSearch.collectionsPromise = Promise.resolve(JSON.parse(stored)));
    } catch (error) {}

    const fetchPage = (page, collected) => fetch(`${root}/collections.json?limit=250&page=${page}`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(({ collections }) => {
        collections
          .filter((collection) => collection.products_count > 0)
          .forEach((collection) => collected.push({
            handle: collection.handle,
            // Some Arabic titles start with a stray "arabic" line left over from translation
            title: collection.title.replace(/^arabic\s*\n/i, '').trim(),
            count: collection.products_count,
            image: collection.image ? { src: collection.image.src, alt: collection.image.alt || '', width: collection.image.width, height: collection.image.height } : null
          }));

        return collections.length === 250 && page < 5 ? fetchPage(page + 1, collected) : collected;
      });

    PredictiveSearch.collectionsPromise = fetchPage(1, [])
      .then((collections) => {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(collections));
        } catch (error) {}
        return collections;
      })
      .catch(() => {
        // Show products only this time and try again on the next search
        PredictiveSearch.collectionsPromise = null;
        return [];
      });

    return PredictiveSearch.collectionsPromise;
  }

  addCollectionResults(section, collections, searchTerm, perPage) {
    const strings = section.querySelector('[data-predictive-search-strings]');
    const results = section.querySelector('.predictive-search-results');
    if (!strings || !results) return;

    // Titles that start with the term first, then a word inside the title, then anywhere
    const term = searchTerm.toLowerCase();
    const matches = collections
      .map((collection) => {
        const title = collection.title.toLowerCase();
        const index = title.indexOf(term);
        const rank = index === 0 ? 0 : index > 0 && /\s/.test(title[index - 1]) ? 1 : index > 0 ? 2 : -1;
        return { collection, rank };
      })
      .filter(({ rank }) => rank >= 0)
      .sort((a, b) => a.rank - b.rank)
      // Half the results setting, as predictive search splits its limit between collections and products
      .slice(0, Math.ceil(perPage / 2))
      .map(({ collection }) => collection);

    if (!matches.length) return;

    const create = (tag, className, text) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    };
    // Shopify already escapes the search terms inside translations, so the attributes arrive escaped twice
    const text = (key) => new DOMParser().parseFromString(strings.dataset[key], 'text/html').documentElement.textContent;
    const countText = (count) => count === 1 ? text('countOne') : text('countOther').replace('9999', count);
    const root = window.routes.root_url.replace(/\/$/, '');

    // Same markup as the collections list in sections/predictive-search.liquid
    const heading = create('p', 'predictive-search__heading h4', text('collections'));
    heading.id = 'predictive-search-collections';

    const list = create('ul', 'predictive-search__results-list list-unstyled');
    list.id = 'predictive-search-results-collections-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', 'predictive-search-collections');

    matches.forEach((collection, index) => {
      const item = create('li', 'predictive-search__list-item');
      item.id = `predictive-search-collection-option-${index + 1}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');

      const link = create('a', 'predictive-search__item predictive-search__item--link link link--text');
      link.href = `${root}/collections/${encodeURIComponent(collection.handle)}`;
      link.tabIndex = -1;

      if (collection.image) {
        const imageUrl = (width) => {
          const url = new URL(collection.image.src, window.location.origin);
          url.searchParams.set('width', width);
          return url.toString();
        };
        const ratio = collection.image.width / collection.image.height || 1;
        const media = create('div', 'media media--adapt');
        media.style.setProperty('--image-ratio-percent', `${100 / ratio}%`);
        const image = create('img');
        image.src = imageUrl(70);
        image.srcset = `${imageUrl(70)} 1x, ${imageUrl(140)} 2x`;
        image.alt = collection.image.alt;
        image.width = 70;
        image.height = Math.round(70 / ratio);
        image.loading = 'lazy';
        const wrapper = create('span', 'predictive-search__image media-wrapper media-wrapper--small');
        media.appendChild(image);
        wrapper.appendChild(media);
        link.appendChild(create('div')).appendChild(wrapper);
      }

      const content = create('div', 'predictive-search__item-content');
      content.appendChild(create('span', 'predictive-search__item-heading h4', collection.title));
      content.appendChild(create('div', 'predictive-search__item-vendor card-article-info caption-with-letter-spacing', countText(collection.count)));
      link.appendChild(content);
      item.appendChild(link);
      list.appendChild(item);
    });

    // Drop the "no results" message when only collections matched
    const noResults = results.querySelector('.title-wrapper');
    if (noResults) noResults.remove();
    results.classList.remove('is-empty');
    results.prepend(heading, list);

    const total = matches.length + results.querySelectorAll('#predictive-search-results-list [data-published-at]').length;
    const liveRegion = section.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) liveRegion.textContent = total === 1 ? text('resultsOne') : text('resultsOther').replace('9999', total);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    this.sortResultsByPublishedDate();
    this.setLiveRegionResults();
    this.open();
  }

  sortResultsByPublishedDate() {
    const list = this.querySelector('#predictive-search-results-list');
    if (!list) return;

    const items = Array.from(list.querySelectorAll('.predictive-search__list-item[data-published-at]'));
    if (!items.length) return;

    // Move the elements themselves rather than setting CSS order, so arrow keys follow what is on screen
    items
      .map((item) => ({ item, date: new Date(item.getAttribute('data-published-at')).getTime() || 0 }))
      .sort((a, b) => b.date - a.date)
      .forEach(({ item }) => list.appendChild(item));

    const searchKeywordsItem = list.querySelector('#predictive-search-option-search-keywords');
    if (searchKeywordsItem) list.appendChild(searchKeywordsItem);
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    document.body.classList.add('predictive-search--focus');
  }

  close() {
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event.preventDefault();
    
    this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');
  }
}

// Shopify's predictive search doesn't support Arabic, so skip straight to the full search there
PredictiveSearch.useFallback = document.documentElement.lang.toLowerCase().startsWith('ar');

customElements.define('predictive-search', PredictiveSearch);
